import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Repeat, MapPin } from "lucide-react";
import { api, imgSrc } from "@/lib/api";

const TIME_AGO = (iso) => {
  if (!iso) return "";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

function TradeCard({ trade }) {
  return (
    <div className="vyrti-card flex items-center gap-3 px-3 py-2.5 min-w-[340px] max-w-[400px] flex-shrink-0" data-testid={`recent-trade-${trade.id}`}>
      {/* Two stacked item thumbs */}
      <div className="flex -space-x-2 flex-shrink-0">
        {trade.offered_item.image ? (
          <img src={imgSrc(trade.offered_item.image)} alt="" className="w-10 h-10 rounded-md object-cover border-2 border-white shadow-sm" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-slate-100 border-2 border-white" />
        )}
        {trade.received_item.image ? (
          <img src={imgSrc(trade.received_item.image)} alt="" className="w-10 h-10 rounded-md object-cover border-2 border-white shadow-sm" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-slate-100 border-2 border-white" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-slate-700 leading-snug truncate">
          <Link to={`/profile/${trade.proposer.user_id}`} className="font-semibold hover:text-[#0654BA]">{trade.proposer.name}</Link>
          <span className="text-slate-400"> traded </span>
          <span className="text-slate-900">{trade.offered_item.title}</span>
        </p>
        <p className="text-[11px] text-slate-500 leading-snug truncate">
          <Repeat className="inline w-3 h-3 mr-1 -mt-0.5 text-[#0654BA]" />
          <span className="text-slate-700">{trade.received_item.title}</span>
          <span className="text-slate-400"> with </span>
          <Link to={`/profile/${trade.receiver.user_id}`} className="font-semibold hover:text-[#0654BA]">{trade.receiver.name}</Link>
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5">
          {trade.received_item.location && <><MapPin className="w-2.5 h-2.5" />{trade.received_item.location}</>}
          <span className="ml-auto text-[#0654BA] font-semibold">{TIME_AGO(trade.accepted_at)}</span>
        </p>
      </div>
    </div>
  );
}

/**
 * Just-Traded social-proof rail.
 * Auto-scrolling marquee of recently completed trades. Pauses on hover.
 */
export default function JustTradedRail() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/social/recent-trades", { params: { limit: 20 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {});
  }, []);

  if (items.length === 0) return null;

  // Duplicate the list so the marquee loop is seamless
  const loop = items.length < 4 ? [...items, ...items, ...items, ...items] : [...items, ...items];

  return (
    <section className="border-y border-slate-200 bg-white overflow-hidden py-3" data-testid="just-traded-rail">
      <div className="section-pad flex items-center gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
          <p className="text-[11px] tracking-widest uppercase font-bold text-slate-600">Just traded</p>
        </div>
        <div className="hairline flex-1 hidden md:block" />
        <div className="flex-1 overflow-hidden relative group">
          <div className="flex gap-3 marquee group-hover:[animation-play-state:paused] w-max" style={{ animationDuration: `${loop.length * 4}s` }}>
            {loop.map((trade, i) => (
              <TradeCard key={`${trade.id}-${i}`} trade={trade} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
